import {
  CalendarFilled,
  CheckCircleFilled,
  DeleteFilled,
  EditFilled,
} from "@ant-design/icons";
import { Button, Progress, Spin, Table } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetGroupUsers2,
  useGetSavingsGoalById,
  useGetSavingsGoalItems,
} from "../../api/api.get";
import { useDeleteSavingsGoalItem } from "../../api/api.delete";
import { useDeleteSavingsGoal } from "../../api/api.delete";
import ConfirmDialog from "../../components/dialog/confirm.dialog";
import { useNotifications } from "../../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useAuthContext } from "../../providers/auth.provider";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { ISavingsGoalItem } from "../../types/types";
import { capitalize } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";
import GoalItemDialog from "./goal.item.dialog";
import EditSavingsGoalDialog from "./edit.savings.goal.dialog";

export default function SavingsGoalViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user } = useAuthContext();
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();

  const { notification, notifySuccess, notifyError } = useNotifications();

  const {
    data: goal,
    isPending: isGoalLoading,
    refetch: refetchGoal,
  } = useGetSavingsGoalById(defaultGroupId, id);

  const {
    data: items,
    isPending: isItemsLoading,
    refetch: refetchItems,
  } = useGetSavingsGoalItems(defaultGroupId, id);

  const { mutateAsync: deleteItemAsync } = useDeleteSavingsGoalItem(
    defaultGroupId,
    id,
  );
  const { mutateAsync: deleteGoalAsync } = useDeleteSavingsGoal(defaultGroupId);

  const { data: groupUsers } = useGetGroupUsers2(defaultGroupId);

  // username -> first name (empty when the member has no first name)
  const memberNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    groupUsers?.forEach((gu) => {
      map[gu.username] = gu.firstName ? capitalize(gu.firstName) : "";
    });
    return map;
  }, [groupUsers]);

  const [showAddItem, setShowAddItem] = useState(false);
  const [editItem, setEditItem] = useState<ISavingsGoalItem>();
  const [deleteItemId, setDeleteItemId] = useState<string>();
  const [showEditGoal, setShowEditGoal] = useState(false);
  const [confirmDeleteGoal, setConfirmDeleteGoal] = useState(false);

  const savedAmount = goal?.savedAmount ?? 0;
  const targetAmount = goal?.targetAmount ?? 0;
  const remaining = Math.max(targetAmount - savedAmount, 0);
  const percent = targetAmount > 0 ? (savedAmount / targetAmount) * 100 : 0;
  const isOwner = goal?.username === user?.username;

  // Monthly / biweekly contribution required to hit the target on time.
  const { monthly, biweekly, daysLeft } = useMemo(() => {
    if (!goal) return { monthly: 0, biweekly: 0, daysLeft: 0 };
    const target = dayjs(goal.targetDate);
    const days = Math.max(target.diff(dayjs(), "day"), 0);
    const monthlyPeriods = Math.max(target.diff(dayjs(), "month"), 1);
    const biweeklyPeriods = Math.max(Math.ceil(days / 14), 1);
    return {
      daysLeft: days,
      monthly: remaining / monthlyPeriods,
      biweekly: remaining / biweeklyPeriods,
    };
  }, [goal, remaining]);

  const handleDeleteItem = () => {
    deleteItemAsync({ itemId: deleteItemId! })
      .then(() => {
        setDeleteItemId(undefined);
        refetchItems();
        refetchGoal();
        notifySuccess({ description: "Contribution deleted." });
      })
      .catch(() => notifyError("Whoops! Please try again."));
  };

  const handleDeleteGoal = () => {
    deleteGoalAsync({ goalId: id! })
      .then(() => {
        notifySuccess({ description: "Goal deleted." });
        navigate("/savings");
      })
      .catch(() => notifyError("Whoops! Please try again."));
  };

  const columns = [
    {
      title: "Amount",
      key: "amount",
      render: (_: any, record: ISavingsGoalItem) => (
        <span className="font-bold text-[#541eff]">
          {currency}
          {toFixedDisplay(record.amount)}
        </span>
      ),
      sorter: (a: ISavingsGoalItem, b: ISavingsGoalItem) => a.amount - b.amount,
    },
    {
      title: "Date",
      dataIndex: "itemDate",
      key: "itemDate",
      render: (date: string) => (
        <span>{dayjs(date).format("DD MMM YYYY")}</span>
      ),
    },
    {
      title: "By",
      key: "username",
      render: (_: any, record: ISavingsGoalItem) => (
        <span>{memberNameMap[record.username]}</span>
      ),
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      render: (note?: string) => <span>{note}</span>,
    },
    {
      title: "",
      width: 90,
      render: (_: any, record: ISavingsGoalItem) => (
        <div className="flex gap-1">
          <Button
            icon={<EditFilled />}
            type="text"
            disabled={record.username !== user?.username}
            onClick={() => setEditItem(record)}
          />
          <Button
            icon={<DeleteFilled />}
            type="text"
            disabled={record.username !== user?.username}
            onClick={() => setDeleteItemId(record.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}

      {showAddItem && (
        <GoalItemDialog
          open={showAddItem}
          groupId={defaultGroupId}
          goalId={id}
          onCancel={() => setShowAddItem(false)}
          onSuccess={() => {
            setShowAddItem(false);
            refetchItems();
            refetchGoal();
            notifySuccess({ description: "Contribution added." });
          }}
          onError={() => notifyError("Whoops! Please try again.")}
        />
      )}

      {editItem && (
        <GoalItemDialog
          open={!!editItem}
          groupId={defaultGroupId}
          goalId={id}
          item={editItem}
          onCancel={() => setEditItem(undefined)}
          onSuccess={() => {
            setEditItem(undefined);
            refetchItems();
            refetchGoal();
            notifySuccess({ description: "Contribution updated." });
          }}
          onError={() => {
            setEditItem(undefined);
            notifyError("Whoops! Please try again.");
          }}
        />
      )}

      {showEditGoal && (
        <EditSavingsGoalDialog
          open={showEditGoal}
          goal={goal}
          onCancel={() => setShowEditGoal(false)}
          onSuccess={() => {
            setShowEditGoal(false);
            refetchGoal();
            notifySuccess({ description: "Goal updated." });
          }}
          onError={() => {
            setShowEditGoal(false);
            notifyError("Whoops! Please try again.");
          }}
        />
      )}

      {deleteItemId && (
        <ConfirmDialog
          open={!!deleteItemId}
          title="Delete this contribution?"
          onCancel={() => setDeleteItemId(undefined)}
          onOk={handleDeleteItem}
        />
      )}

      {confirmDeleteGoal && (
        <ConfirmDialog
          open={confirmDeleteGoal}
          title="Delete this savings goal?"
          subtitle="This will also delete all contributions for this goal."
          onCancel={() => setConfirmDeleteGoal(false)}
          onOk={handleDeleteGoal}
        />
      )}

      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Button
            type="link"
            className="mb-2"
            onClick={() => navigate("/savings")}
          >
            Back
          </Button>

          {isGoalLoading && <Spin size="large" />}

          {goal && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: summary */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="bg-gradient-to-br from-primary/10 to-blue-500/10">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between gap-2">
                      <span>{capitalize(goal.name)}</span>
                      {goal.completed && (
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircleFilled /> Completed
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Target {currency}
                      {toFixedDisplay(targetAmount)} by{" "}
                      {dayjs(goal.targetDate).format("DD MMM YYYY")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Progress
                      percent={Math.min(Math.round(percent), 100)}
                      status={goal.completed ? "success" : "active"}
                    />
                    <div className="mt-3 flex justify-between text-sm">
                      <span className="text-muted-foreground">Saved</span>
                      <span className="font-bold">
                        {currency}
                        {toFixedDisplay(savedAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Remaining</span>
                      <span className="font-bold">
                        {currency}
                        {toFixedDisplay(remaining)}
                      </span>
                    </div>

                    {isOwner && (
                      <div className="flex gap-2 mt-4">
                        <Button
                          icon={<EditFilled />}
                          onClick={() => setShowEditGoal(true)}
                        >
                          Edit
                        </Button>
                        <Button
                          danger
                          icon={<DeleteFilled />}
                          onClick={() => setConfirmDeleteGoal(true)}
                        >
                          Delete
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarFilled className="text-primary" /> To stay on
                      track
                    </CardTitle>
                    <CardDescription>
                      {goal.completed
                        ? "You've reached this goal 🎉"
                        : `${daysLeft} days left`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">
                        Monthly
                      </span>
                      <span className="font-bold">
                        {currency}
                        {toFixedDisplay(monthly)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">
                        Bi-weekly
                      </span>
                      <span className="font-bold">
                        {currency}
                        {toFixedDisplay(biweekly)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right: contributions */}
              <div className="lg:col-span-2">
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between gap-2">
                      <span>Contributions</span>
                      <Button type="link" onClick={() => setShowAddItem(true)}>
                        Add Contribution
                      </Button>
                    </CardTitle>
                    <CardDescription>
                      {items?.length ?? 0} contribution
                      {items?.length !== 1 ? "s" : ""} · Total {currency}
                      {toFixedDisplay(savedAmount)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table
                      dataSource={items}
                      columns={columns}
                      rowKey="id"
                      loading={isItemsLoading}
                      pagination={{ pageSize: 50 }}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
